import React, { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Card, Container, Row, Col, Button, Form, Alert } from "react-bootstrap"
import {
  FaShieldAlt,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaUserCheck,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaVenusMars,
} from "react-icons/fa"
import apiService from "../../services/api"

export default function AdminRegister() {
  const navigate = useNavigate()
  const [showAlert, setShowAlert] = useState({ show: false, variant: "", message: "" })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    password: "",
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setShowAlert({ show: false, variant: "", message: "" })
    setLoading(true)

    try {
      const userData = { ...formData, role: "admin" }
      const response = await apiService.register(userData)

      if (response.success) {
        setShowAlert({
          show: true,
          variant: "success",
          message: "Admin Registration Successful! Redirecting to login...",
        })
        setTimeout(() => navigate("/admin/login"), 1500)
      } else {
        setShowAlert({
          show: true,
          variant: "danger",
          message: response.error?.message || "Registration failed",
        })
      }
    } catch (error) {
      setShowAlert({ show: true, variant: "danger", message: "Network error. Please try again." })
    } finally {
      setLoading(false)
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
    }
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center position-relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
      }}
    >
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100">
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "200px",
            height: "200px",
            background: "white",
            top: "10%",
            right: "15%",
            animation: "float 6s ease-in-out infinite",
          }}
        ></div>
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "150px",
            height: "150px",
            background: "white",
            bottom: "15%",
            left: "10%",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        ></div>
      </div>

      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            {/* Header */}
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{
                  width: "80px",
                  height: "80px",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <FaShieldAlt size={32} className="text-white" />
              </div>
              <h2 className="h3 fw-bold text-white mb-2">Admin Registration</h2>
              <p className="text-white opacity-90">Create a secure account for Aidify administrators</p>
            </div>

            {showAlert.show && (
              <Alert
                variant={showAlert.variant}
                className="mb-4 border-0 rounded-4"
                style={{
                  backdropFilter: "blur(20px)",
                  backgroundColor:
                    showAlert.variant === "success" ? "rgba(5, 150, 105, 0.9)" : "rgba(220, 38, 38, 0.9)",
                }}
              >
                <div className="text-white fw-medium">{showAlert.message}</div>
              </Alert>
            )}

            {/* Register Form */}
            <Card
              className="border-0 rounded-4 overflow-hidden"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
            >
              <Card.Header className="bg-transparent border-0 p-4">
                <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                  Create Admin Account
                </h5>
                <p className="text-muted mb-0">Fill in the details to register as an administrator</p>
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handleSubmit}>
                  {/* Name */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Full Name</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaUserCheck size={16} />
                      </div>
                      <Form.Control
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{ backgroundColor: "#f0fdfa", fontSize: "16px" }}
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                  </Form.Group>

                  {/* Email */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Email</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaEnvelope size={16} />
                      </div>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{ backgroundColor: "#f0fdfa", fontSize: "16px" }}
                        placeholder="Enter admin email"
                        required
                      />
                    </div>
                  </Form.Group>

                  {/* Phone */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Phone</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaPhone size={16} />
                      </div>
                      <Form.Control
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{ backgroundColor: "#f0fdfa", fontSize: "16px" }}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </Form.Group>

                  {/* Date of Birth */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Date of Birth</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaCalendarAlt size={16} />
                      </div>
                      <Form.Control
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{ backgroundColor: "#f0fdfa", fontSize: "16px" }}
                      />
                    </div>
                  </Form.Group>

                  {/* Gender */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Gender</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaVenusMars size={16} />
                      </div>
                      <Form.Select
                        value={formData.gender}
                        onChange={(e) => handleInputChange("gender", e.target.value)}
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{ backgroundColor: "#f0fdfa", fontSize: "16px" }}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </div>
                  </Form.Group>

                  {/* Password */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Password</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaLock size={16} />
                      </div>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        className="ps-5 pe-5 py-3 border-0 rounded-3"
                        style={{ backgroundColor: "#f0fdfa", fontSize: "16px" }}
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-3 text-muted p-0"
                        style={{ border: "none", background: "none" }}
                      >
                        {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                  </Form.Group>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-100 py-3 rounded-3 fw-bold border-0 mb-4"
                    style={{
                      background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                      boxShadow: "0 8px 25px rgba(8, 145, 178, 0.4)",
                      transform: "translateY(0)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <FaShieldAlt className="me-2" />
                    Register as Admin
                  </Button>

                  <div className="text-center">
                    <p className="text-muted mb-3">
                      Already have account?{" "}
                      <Link to="/admin/login" className="text-decoration-none" style={{ color: "#0891b2" }}>
                        Sign In here
                      </Link>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Security Features */}
            <div className="text-center mt-4">
              <Row className="g-3">
                <Col xs={4}>
                  <div className="d-flex flex-column align-items-center">
                    <FaShieldAlt className="text-white opacity-75 mb-1" size={16} />
                    <small className="text-white opacity-75">Encrypted</small>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="d-flex flex-column align-items-center">
                    <FaLock className="text-white opacity-75 mb-1" size={16} />
                    <small className="text-white opacity-75">Secure</small>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="d-flex flex-column align-items-center">
                    <FaUserCheck className="text-white opacity-75 mb-1" size={16} />
                    <small className="text-white opacity-75">Verified</small>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  )
}
